/* eslint-disable react/jsx-no-useless-fragment */
import { Children, PropsWithChildren, ReactElement, cloneElement } from 'react';

import { useFeatureFlag } from './useFeatureFlag';
import { FeatureFlagProps, OnOffProps } from './types';

/**
 * Conditionally render children based on feature flag.
 * @param {string} feature The key of the feature flag
 * @example
 * import { FeatureFlag, On, Off } from './FeatureFlag';
 * //...
 * <FeatureFlag feature="DEVICE_INVENTORY">
 *   <On>
 *     This will render when the flag is enabled.
 *   </On>
 *   <Off>
 *     This will render when the flag is disabled.
 *   </Off>
 * </FeatureFlag>
 */
export const FeatureFlag = ({feature, children}: PropsWithChildren<FeatureFlagProps>) => {
  const featureFlagEnabled = useFeatureFlag(feature);
  const toggledChildren = Children.map(children, (child) =>
    cloneElement(child as ReactElement, { featureFlagEnabled })
  );
  return <>{toggledChildren}</>;
};

/**
 * Only render children if the feature flag is enabled. Always a child of <FeatureFlag>.
 * @param {boolean} featureFlagEnabled Whether or not the flag is enabled. Passed from the parent.
 */
export const On = ({ featureFlagEnabled, children }: PropsWithChildren<OnOffProps>) => (
  <>{featureFlagEnabled && children}</>
);

/**
 * Only render children if the feature flag is disabled. Always a child of <FeatureFlag>.
 * @param {boolean} featureFlagEnabled Whether or not the flag is enabled. Passed from the parent.
 */
export const Off = ({ featureFlagEnabled, children }: PropsWithChildren<OnOffProps>) => (
  <>{!featureFlagEnabled && children}</>
);

export * from './types';
export * from './useFeatureFlag';
